import { Spinner } from '@geist-ui/react'
import { captureException } from '@sentry/nextjs'
import * as React from 'react'
import { animated, useSpring } from 'react-spring'
import styled from 'styled-components'
import { Follow, Unfollow } from '../../../data/Follow'
import useAuth from '../../../hooks/auth'
import { useFirebaseAuthGuard } from '../../../hooks/firebase'

// ============================ Styled components ==============================

const OuterDiv = styled(animated.div)<{ isFollowing?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  border-radius: 10px;
  padding: 5px 0;
  width: 100px;
  cursor: pointer;
  background: ${(props) =>
    props.isFollowing ? 'linear-gradient(rgb(55, 55, 55), rgb(25, 25, 25))' : '#fff'};
  border: ${(props) => (props.isFollowing ? '1px solid #323232' : '')};

  @media (min-width: 43em) {
    width: 140px;
  }
`

const Title = styled.div<{ isFollowing?: boolean }>`
  color: ${(props) => (props.isFollowing ? '#fff' : 'black')};
  font-weight: 700;
  display: flex;
  margin-left: 5px;
  margin-right: 5px;
  font-size: 17px;

  // tablet
  @media (max-width: 62em) {
    font-size: 17px;
  }

  // mobile
  @media (max-width: 43em) {
    font-size: 15px;
  }
`

const SmallSpinner = styled(Spinner)`
  min-width: 24px;
  min-height: 24px;
  span {
    background-color: black !important;
  }
`

const BaseFollowButton = ({
  address,
  isFollowing,
  isUpdating,
  setIsUpdating,
  setIsFollowing,
  onUpdate,
}: {
  address: string
  isFollowing: boolean | undefined
  isUpdating: boolean | undefined
  setIsUpdating: Function
  setIsFollowing: Function
  onUpdate: (follow: boolean) => void
}): JSX.Element => {
  const [followHover, setFollowHover] = React.useState(false)
  const { profile } = useAuth()
  const followHoverState = useSpring({
    boxShadow: followHover ? '0px 5px 25px rgba(255,255,255,0.2)' : '0px 0px 0px rgba(0,0,0,0)',
    transform: followHover ? `translate3d(0,-5%,0)` : `translate3d(0,0%,0)`,
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
      velocity: 0,
    },
  })
  const handleFollow = React.useCallback(async () => {
    if (address === profile?.address) {
      return
    }
    try {
      setIsUpdating(true)
      if (isFollowing) {
        await Unfollow(address)
        onUpdate(false)
      } else {
        await Follow(address)
        onUpdate(true)
      }
    } catch (err) {
      console.error(err)
      captureException(err)
    } finally {
      setIsUpdating(false)
    }
    setIsFollowing(!isFollowing)
  }, [address, profile?.address, setIsFollowing, isFollowing, setIsUpdating, onUpdate])
  const guardedFollow = useFirebaseAuthGuard(handleFollow)
  const guardedWalletConnect = useFirebaseAuthGuard(guardedFollow)
  const handleClick = React.useCallback(
    (e) => {
      e.stopPropagation()

      guardedWalletConnect()
    },
    [guardedWalletConnect]
  )

  return (
    <OuterDiv
      isFollowing={isFollowing && !isUpdating}
      onMouseEnter={() => setFollowHover(true)}
      onMouseLeave={() => setFollowHover(false)}
      style={followHoverState}
      onClick={handleClick}
    >
      <Title isFollowing={isFollowing && !isUpdating}>
        {isUpdating ? <SmallSpinner /> : isFollowing ? 'Following' : 'Follow'}
      </Title>
    </OuterDiv>
  )
}

export default BaseFollowButton
